import { Component, ErrorInfo, ReactNode } from 'react';

import { reportCoralogixError } from '@sunflower-games/coralogix-rum';

import './ErrorBoundary.scss';
import { sendMessage } from '../../hooks/useMessageSender';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });

    // Log to New Relic if available
    if (window.newrelic) {
      window.newrelic.noticeError(error, {
        errorInfo: errorInfo.componentStack,
      });
    }

    reportCoralogixError(error, { componentStack: errorInfo.componentStack });

    setTimeout(() => {
      if (!import.meta.env.DEV && this.state.error) {
        sendMessage({ type: 'reload' });
      }
    }, 10000);
  }

  handleReload = (): void => {
    window.location.reload();
    sendMessage({ type: 'reload' });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="error-boundary">
          <div className="error-boundary__content">
            <h1 className="error-boundary__title">Oops! Something went wrong</h1>
            <p className="error-boundary__message">
              We&apos;re sorry, but something unexpected happened. Please try reloading the page.
            </p>
            <button className="error-boundary__button" onClick={this.handleReload}>
              Reload Page
            </button>
            {import.meta.env.DEV && this.state.error && (
              <details className="error-boundary__details">
                <summary>Error Details (Dev Only)</summary>
                <pre className="error-boundary__stack">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
